import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert, Box, Button, CircularProgress, Collapse, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography,
} from '@mui/material';
import { StatusPill } from '@norvix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { commissionTransfersApi, type CommissionTransfer } from '../../api/commission-transfers.api.js';

import { ResolveTransferDialog } from './ResolveTransferDialog.js';
import { MODE_LABEL, STATUS_LABEL, STATUS_TONE, centsLabel, dateTimeUtc } from './transfer-format.js';

const amount = { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } as const;

function TransferItems({ transfer }: { transfer: CommissionTransfer }): JSX.Element {
  const query = useQuery({ queryKey: ['commission-transfer-items', transfer.id], queryFn: () => commissionTransfersApi.items(transfer.id) });
  if (query.isLoading) return <Box sx={{ p: 2 }} role="status"><CircularProgress size={20} /></Box>;
  if (query.isError || !query.data) return <Alert severity="error" sx={{ m: 1 }}>The commissions in this transfer could not be loaded.</Alert>;
  const { linked, items } = query.data;
  return <Box sx={{ p: { xs: 1, sm: 2 } }}>
    <Typography variant="body2" sx={{ mb: 1, overflowWrap: 'anywhere' }}>
      From {transfer.fromIban || 'unknown'} → To {transfer.toIban || 'not set'}
      {transfer.mode === 'CALCULATE_ONLY' ? ' (calculated only, nothing moved)' : ''}
    </Typography>
    {!linked && <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
      Nothing was moved, so no commissions are tied to this row. Shown: commissions still pending under its cut-off.
    </Typography>}
    {items.length === 0 ? <Typography variant="body2">No commissions.</Typography> :
      <Table size="small" aria-label="Commissions in this transfer">
        <TableHead><TableRow><TableCell>Posted (UTC)</TableCell><TableCell>Fee</TableCell><TableCell>Entry</TableCell>
          <TableCell align="right">Amount</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
        <TableBody>{items.map((item) => <TableRow key={item.id}>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTimeUtc(item.postedAt)}</TableCell>
          <TableCell>{item.feeName}</TableCell>
          <TableCell>{item.kind}</TableCell>
          <TableCell align="right" sx={amount}>{centsLabel(item.amountCents, item.currency)}</TableCell>
          <TableCell>{item.status}</TableCell>
        </TableRow>)}</TableBody>
      </Table>}
  </Box>;
}

interface RowProps { transfer: CommissionTransfer; canResolve: boolean }

function TransferRow({ transfer, canResolve }: RowProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const unknown = transfer.status === 'EXECUTION_UNKNOWN';
  const label = `${open ? 'Hide' : 'Show'} the ${String(transfer.itemCount)} commissions of this transfer`;
  return <>
    <TableRow hover sx={{ '& > td': { borderBottom: open ? 'unset' : undefined }, ...(unknown ? { bgcolor: 'rgba(255, 82, 82, 0.08)' } : {}) }}>
      <TableCell padding="checkbox">
        <IconButton size="small" aria-label={label} aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTimeUtc(transfer.createdAt)}
        <Typography variant="caption" color="text.secondary" display="block">cut-off {dateTimeUtc(transfer.cutoffAt)}</Typography></TableCell>
      <TableCell>{transfer.kind}</TableCell>
      <TableCell align="right" sx={{ ...amount, fontWeight: 600 }}>{centsLabel(transfer.amountCents, transfer.currency)}</TableCell>
      <TableCell align="right" sx={amount}>{transfer.offsetCents !== undefined && transfer.offsetCents !== '0'
        ? centsLabel(transfer.offsetCents, transfer.currency) : '—'}</TableCell>
      <TableCell align="right">{transfer.itemCount}</TableCell>
      <TableCell>
        <Tooltip title={transfer.lastError ?? (typeof transfer.guardDetails?.['reason'] === 'string' ? transfer.guardDetails['reason'] : '')}>
          <span><StatusPill tone={STATUS_TONE[transfer.status]}>{STATUS_LABEL[transfer.status]}</StatusPill></span>
        </Tooltip>
        {unknown && <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5, maxWidth: 240 }}>
          Blocks new {transfer.currency} transfers until resolved.</Typography>}
        {unknown && canResolve && <Button size="small" color="error" variant="outlined" sx={{ mt: 0.5 }}
          onClick={() => setResolving(true)}>Resolve</Button>}
        {resolving && <ResolveTransferDialog transfer={transfer} onClose={() => setResolving(false)} />}
      </TableCell>
      <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {transfer.providerTransactionUuid ?? '—'}</TableCell>
      <TableCell>{MODE_LABEL[transfer.mode]}</TableCell>
    </TableRow>
    <TableRow>
      <TableCell colSpan={9} sx={{ py: 0 }}>
        <Collapse in={open} timeout="auto" unmountOnExit><TransferItems transfer={transfer} /></Collapse>
      </TableCell>
    </TableRow>
  </>;
}

export function TransferLogTable({ transfers, canResolve }: { transfers: CommissionTransfer[]; canResolve: boolean }): JSX.Element {
  return <TableContainer>
    <Table aria-label="Transfers to the revenue account" sx={{ minWidth: 880 }}>
      <TableHead><TableRow>
        <TableCell padding="checkbox" /><TableCell>Date</TableCell><TableCell>Kind</TableCell><TableCell align="right">Amount</TableCell>
        <TableCell align="right">Covered GuruPay fees</TableCell>
        <TableCell align="right">Commissions</TableCell><TableCell>Status</TableCell><TableCell>GuruPay ref</TableCell><TableCell>Mode</TableCell>
      </TableRow></TableHead>
      <TableBody>
        {transfers.map((transfer) => <TransferRow key={transfer.id} transfer={transfer} canResolve={canResolve} />)}
        {transfers.length === 0 && <TableRow><TableCell colSpan={9}>No transfers yet.</TableCell></TableRow>}
      </TableBody>
    </Table>
  </TableContainer>;
}
